
import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from '@/components/ui/label';
import { DollarSign, Utensils, Briefcase, Sun } from 'lucide-react';

interface RestaurantDetailsProps {
  place: any;
  isEditing: boolean;
  onInputChange: (field: string, value: any) => void;
}

export function RestaurantDetails({ place, isEditing, onInputChange }: RestaurantDetailsProps) {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold flex items-center gap-2">
        <Utensils className="w-5 h-5" />
        Restaurant Details
      </h3>
      
      {isEditing ? (
        <>
          <div className="space-y-2">
            <Label>Meal Price Range ($)</Label>
            <Input
              type="number"
              value={place.meal_price_range || ''}
              onChange={(e) => onInputChange('meal_price_range', e.target.value)}
              placeholder="Average meal cost"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Cuisine Specialties</Label>
            <Input
              value={place.cuisine_specialties?.join(', ') || ''}
              onChange={(e) => onInputChange('cuisine_specialties', e.target.value.split(',').map(s => s.trim()))}
              placeholder="Enter specialties separated by commas"
            />
          </div>

          <div className="space-y-2">
            <Label>Dress Code</Label>
            <Select
              value={place.dress_code || ''}
              onValueChange={(value) => onInputChange('dress_code', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select dress code" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="casual">Casual</SelectItem>
                <SelectItem value="smart_casual">Smart Casual</SelectItem>
                <SelectItem value="business_casual">Business Casual</SelectItem>
                <SelectItem value="formal">Formal</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              checked={place.outdoor_seating}
              onCheckedChange={(checked) => onInputChange('outdoor_seating', checked)}
            />
            <Label>Outdoor Seating Available</Label>
          </div>
        </>
      ) : (
        <div className="space-y-4">
          {place.meal_price_range && (
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-muted-foreground" />
              <span>Average meal: ${place.meal_price_range}</span>
            </div>
          )}
          
          {place.cuisine_specialties?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {place.cuisine_specialties.map((specialty: string) => (
                <Badge key={specialty} variant="secondary">
                  {specialty}
                </Badge>
              ))}
            </div>
          )}

          {place.dress_code && (
            <div className="flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-muted-foreground" />
              <span>Dress code: {place.dress_code.replace('_', ' ')}</span>
            </div>
          )}

          {place.outdoor_seating && (
            <div className="flex items-center gap-2">
              <Sun className="w-4 h-4 text-muted-foreground" />
              <span>Outdoor seating available</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
